import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { environment } from '../../../../../environments/environment';
import { currentUser } from '../../../../core/auth';
import { AppState } from '../../../../core/reducers';
import { QueryParamsModel, QueryResultsModel } from '../../../../core/_base/crud';
import {
  Form,
  FormFeatureLinks,
  FieldSynonymLink,
  FieldTemplateMap,
  FormTransactionLink,
} from '../models';
import { TransactionSrc, TransactionType } from '../enums';

@Injectable({ providedIn: 'root' })
export class FormService {
  private companyId;
  private baseUrl = environment.FORMS_CONCEPTS_BASE;

  constructor(private http: HttpClient, private store: Store<AppState>) {
    this.store.select(currentUser).subscribe((user) => (this.companyId = user.companyID || 0));
  }

  private static getFormPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
    if (filter.teamId) fromString += `&team_id=${filter.teamId}`;
    if (filter.system) fromString += `&system=${filter.system}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.version?.toString()) fromString += `&version=${filter.version}`;
    }

    return fromString;
  }

  private static getFormTemplateMapPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `model_type=FORM&page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.formId) fromString += `&model_id=${filter.formId}`;
    if (filter.templateId) fromString += `&template_id=${filter.templateId}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.formName) fromString += `&model_name_has=${filter.formName}`;
      if (filter.templateName) fromString += `&template_name_has=${filter.templateName}`;
    }

    return fromString;
  }

  getFormPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({ fromString: FormService.getFormPageQueryString(queryParams) });

    return this.http.get<QueryResultsModel>(
      `${this.baseUrl}/companies/${queryParams.filter?.isGlobal ? 0 : this.companyId}/forms`,
      { params },
    );
  }

  findForms(filter = {}, searchTerm: string): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel(
      { ...filter, searchTerm, isActive: true },
      'asc',
      'name',
      0,
      25,
    );

    return this.getFormPage(queryParams);
  }

  getFormById(
    formId: number,
    withSynonymValues?: boolean,
    version?: number,
  ): Observable<{ data: Form }> {
    let fromString = '';

    if (withSynonymValues) fromString += `&include_synonym_values=${withSynonymValues}`;
    if (version?.toString()) fromString += `&version=${version}`;

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: Form }>(
      `${this.baseUrl}/companies/${this.companyId}/forms/${formId}`,
      { params },
    );
  }

  createForm(form: Form, importedFormId: number): Observable<HttpResponse<{ data: Form }>> {
    let fromString = '';

    if (importedFormId) fromString += `&imported_form_id=${importedFormId}`;

    const params = new HttpParams({ fromString });

    return this.http.post<{ data: Form }>(
      `${this.baseUrl}/companies/${this.companyId}/forms`,
      form,
      { params, observe: 'response' },
    );
  }

  updateForm(form: Form): Observable<HttpResponse<{ data: Form }>> {
    return this.http.put<{ data: Form }>(
      `${this.baseUrl}/companies/${this.companyId}/forms/${form.id}`,
      form,
      { observe: 'response' },
    );
  }

  updateFormStatus(form: Form): Observable<HttpResponse<void>> {
    return this.http.put<void>(
      `${this.baseUrl}/companies/${this.companyId}/forms/${form.id}/change-status`,
      { is_active: form.is_active },
      { observe: 'response' },
    );
  }

  getFormFeatureLinks(formId: number): Observable<{ data: FormFeatureLinks }> {
    return this.http.get<{ data: FormFeatureLinks }>(
      `${this.baseUrl}/companies/${this.companyId}/forms/${formId}/knostos-links`,
    );
  }

  getFormSynonymLinks(form: Form): Observable<{ data: { links: FieldSynonymLink[] } }> {
    const params = new HttpParams({
      fromString: `model_type=FORM&company_id=${this.companyId}&model_id=${form.id}&model_version=${form.version}`,
    });

    return this.http.get<{ data: { links: FieldSynonymLink[] } }>(
      `${this.baseUrl}/dictionaries/synonyms/links`,
      { params },
    );
  }

  getFormFieldsKeys(form: Form): Observable<string[]> {
    return this.getFormSynonymLinks(form).pipe(
      map((res) => res?.data),
      map((res) => res?.links),
      map((links) => links?.map((l) => l?.field_name)),
    );
  }

  updateFormSynonymLinks(form: Form, links: FieldSynonymLink[]): Observable<HttpResponse<void>> {
    const params = new HttpParams({
      fromString: `model_type=FORM&company_id=${this.companyId}&model_id=${form.id}&model_version=${form.version}`,
    });

    return this.http.put<void>(
      `${this.baseUrl}/dictionaries/synonyms/links`,
      { links },
      { params, observe: 'response' },
    );
  }

  getFormTemplateMapPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: FormService.getFormTemplateMapPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${this.baseUrl}/companies/${this.companyId}/docmosis/links`,
      { params },
    );
  }

  findFormTemplateMaps(formId: number): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel({ formId }, 'desc', 'id', 0, 10);

    return this.getFormTemplateMapPage(queryParams);
  }

  createFormTemplateMap(
    formTemplateMap: FieldTemplateMap,
  ): Observable<HttpResponse<{ data: FieldTemplateMap }>> {
    return this.http.post<{ data: FieldTemplateMap }>(
      `${this.baseUrl}/companies/${this.companyId}/docmosis/links`,
      formTemplateMap,
      {
        observe: 'response',
      },
    );
  }

  updateFormTemplateMap(
    formTemplateMap: FieldTemplateMap,
  ): Observable<HttpResponse<{ data: FieldTemplateMap }>> {
    return this.http.put<{ data: FieldTemplateMap }>(
      `${this.baseUrl}/companies/${this.companyId}/docmosis/links/${formTemplateMap.id}`,
      formTemplateMap,
      { observe: 'response' },
    );
  }

  getFormTransactionLink(
    transactionId: number,
    transactionType: TransactionType,
    transactionSrc: TransactionSrc,
  ): Observable<{ data: FormTransactionLink }> {
    const fromString = `transaction_id=${transactionId}&transaction_type=${transactionType}&source=${transactionSrc}`;
    const params = new HttpParams({ fromString });

    return this.http.get<{ data: FormTransactionLink }>(
      `${this.baseUrl}/companies/${this.companyId}/forms/transaction-links`,
      { params },
    );
  }
}
